import React, { useEffect, useState, useRef } from 'react';
import { duration } from 'moment';
import { isNil } from 'lodash';
import { getSecondaryOpenVideo } from 'store/secondaryViewSlice';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { PlayerEvent } from 'bitmovin-player';

interface AddFormProps {
  channelId: string;
  onSubmit: any;
  textinputPlaceholder: string;
  selected: number;
}

function AddForm({ channelId, onSubmit, textinputPlaceholder, selected }: AddFormProps) {
  const [content, setContent] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const secondaryViewVideos = useSelector((state: RootState) => state.secondaryChannelVideos.channels);
  const [time, setTime] = useState(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const handleTextAreaFocus = () => {
    const video = secondaryViewVideos[channelId as string];
    const time = video?.playerRef.getCurrentTime();
    setTime(time);
  };

  const handleClear = () => {
    setContent('');
  };

  const handleSubmit = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    onSubmit(content, time).then(() => {
      setIsSubmitting(false);
      setContent('');
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (textAreaRef.current && !textAreaRef.current.contains(event.target as Node)) {
        textAreaRef.current.blur(); // This will remove focus from the textarea
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const video = secondaryViewVideos[channelId as string];
    const onSeeked = () => {
      const currentTime = video?.playerRef.getCurrentTime();
      setTime(currentTime);
    };

    if (video && video.playerRef) {
      // Define the handler for the seeked event
      video.playerRef.on(PlayerEvent.Seeked, onSeeked);
      const currentTime = video.playerRef.getCurrentTime();
      if (currentTime) {
        setTime(currentTime);
      }
    }
    return () => {
      if (textAreaRef.current) {
        (textAreaRef.current as any) = null;
      }
      setContent('');
      if (video?.playerRef) {
        video.playerRef.off(PlayerEvent.Seeked, onSeeked); // Clean up event listener
      }
    };
  }, [channelId, selected]);

  // useEffect(() => {
  //   const video = secondaryViewVideos[channelId as string];
  //   if (video && video?.selectedShot !== undefined) {
  //     setContent('');
  //   }
  // }, [secondaryViewVideos]);

  return (
    <div className="w-full pb-3 text-[13px] text-[#111]">
      <div className="mx-5 mb-[15px] pb-3 bg-white border shadow-note border-line rounded-md w-[calc(100%-40px)]">
        <textarea
          ref={textAreaRef}
          className="bg-transparent px-3 py-3 w-full text-sm border-0 border-line focus:ring-offset-0 focus:ring-0 focus:ring-line rounded-md resize-none"
          id="note-content-text"
          name="content"
          placeholder={textinputPlaceholder}
          onFocus={handleTextAreaFocus}
          rows={3}
          spellCheck="false"
          value={content}
          onChange={(e) => setContent(e.target.value)}
        ></textarea>
        <div className="text-sm ml-1 flex justify-between items-center">
          <span className="text-[#0979b7] ml-2">
            <span className="text-[#111]">Synched to </span>
            {!isNil(time) && time > 3600
              ? duration(time, 'seconds').format('h:mm:ss', { trim: false })
              : duration(time, 'seconds').format('m:ss', { trim: false })}
          </span>
          <div className="ml-auto mr-3">
            <>
              <button
                className={`peer-focus:block bg-gray-100 text-black mr-2 px-2 py-1 rounded-md ${
                  content ? 'visible' : 'hidden'
                }`}
                onClick={handleClear}
              >
                Clear
              </button>
              <button
                className={`peer-focus:block px-2 py-1 rounded-md  bg-[#0979b7] text-white`}
                onClick={handleSubmit}
                disabled={!content || isSubmitting}
              >
                Save
              </button>
            </>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddForm;
